import { Box, Typography } from '@mui/material';
import CustomButton from '../../../components/@extended/CustomButton';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

// Helper function to find step status from stepStatusData (handles both array and object formats)
function findStepStatus(stepStatusData, stepKey) {
  if (!stepStatusData) return null;

  // If stepStatusData has a stepStatus array (from subjectDetailWithSteps structure)
  if (stepStatusData.stepStatus && Array.isArray(stepStatusData.stepStatus)) {
    return stepStatusData.stepStatus.find((step) => step.stepKey === stepKey);
  }

  // If stepStatusData is an array of steps
  if (Array.isArray(stepStatusData)) {
    return stepStatusData.find((step) => step.stepKey === stepKey);
  }

  // If stepStatusData is a single step object and stepKey matches
  if (stepStatusData.stepKey === stepKey) {
    return stepStatusData;
  }

  // If no stepKey specified, return the stepStatusData itself (assuming it's a single step)
  if (!stepKey) {
    return stepStatusData;
  }

  return null;
}

// Helper function to get the current status (step status or section status)
function getCurrentStatus({ stepStatusData, sectionKey, subSectionKey, stepKey }) {
  // First, find the matching step if stepKey is provided
  const step = stepKey ? findStepStatus(stepStatusData, stepKey) : stepStatusData;

  if (!step) {
    return null;
  }

  // If sectionKey is provided, try to get section status
  if (sectionKey && step.sectionStatuses) {
    const sectionStatus = step.sectionStatuses.find((section) => section.sectionKey === sectionKey);
    if (sectionStatus) {
      // If subSectionKey is provided, try to get subSection status
      if (subSectionKey && sectionStatus.subSectionStatuses) {
        const subSectionStatus = sectionStatus.subSectionStatuses.find(
          (subSection) => subSection.subSectionKey === subSectionKey
        );
        if (subSectionStatus) {
          return subSectionStatus.status;
        }
      }
      return sectionStatus.status;
    }
  }
  // Fall back to step status
  return step.status;
}

const ButtonField = (props) => {
  const { label, readOnly, stepStatusData, field, sectionKey, subSectionKey } = props;
  const signatureUploading = useSelector((state) => state.application.dynamicForm.signatureUploading);

  const isDisabled = useMemo(() => {
    // Check if enabledFormStatus is configured (button disabled until status matches)
    if (field?.enabledFormStatus && Array.isArray(field.enabledFormStatus) && field.enabledFormStatus.length > 0) {
      const isStatusEnabled = field.enabledFormStatus.some((statusConfig) => {
        const configSectionKey = statusConfig.sectionKey !== undefined ? statusConfig.sectionKey : sectionKey;
        const configSubSectionKey =
          statusConfig.subSectionKey !== undefined ? statusConfig.subSectionKey : subSectionKey;
        const configStatus = getCurrentStatus({
          stepStatusData,
          sectionKey: configSectionKey,
          subSectionKey: configSubSectionKey === '' ? undefined : configSubSectionKey,
          stepKey: statusConfig.stepKey,
        });

        if (configStatus && statusConfig.statuses.includes(configStatus)) {
          return true;
        }
        return false;
      });

      return !isStatusEnabled;
    }

    if (field?.disabledFormStatus && Array.isArray(field.disabledFormStatus) && field.disabledFormStatus.length > 0) {
      return field.disabledFormStatus.some((statusConfig) => {
        const configSectionKey = statusConfig.sectionKey !== undefined ? statusConfig.sectionKey : sectionKey;
        const configSubSectionKey =
          statusConfig.subSectionKey !== undefined ? statusConfig.subSectionKey : subSectionKey;
        const configStatus = getCurrentStatus({
          stepStatusData,
          sectionKey: configSectionKey,
          subSectionKey: configSubSectionKey === '' ? undefined : configSubSectionKey,
          stepKey: statusConfig.stepKey,
        });

        if (configStatus && statusConfig.statuses.includes(configStatus)) {
          return true;
        }
        return false;
      });
    }

    // const currentStatus = getCurrentStatus({ stepStatusData, sectionKey, subSectionKey });
    // return currentStatus === 'Completed';
    return false;
  }, [stepStatusData, field, sectionKey, subSectionKey]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'end' }}>
      <CustomButton type="submit" variant="contained" color="primary" disabled={readOnly || isDisabled || signatureUploading}>
        {label}
      </CustomButton>
    </Box>
  );
};

export default ButtonField;
